import type { Dispatch, RefObject, SetStateAction } from "react";
import TableContainer from "../../../../components/TableContainer/TableContainer";
import { CancelPresentation, ContentCopy, CurrencyExchange, ArrowDropDown, ArrowDropUp, LocalAtm } from "@mui/icons-material";
import type { IFinancialEntry, Iordem } from "../../../../types/financial";
import Button from "../../../../components/Button/Button";
import styles from "./IncomingPayments.module.css";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../../utils/useIsMobile";

type FinancialStatusOrType = "aberto" | "pago" | "atrasado" | "cancelado" | "pagar" | "receber";
interface IStatusInfo { text: string; color: string; }

interface prop {
  tbodyRef: RefObject<any>;
  rows: IFinancialEntry[];
  handleCancelEntry: (uid: string) => void;
  statusMapper: (status: FinancialStatusOrType | undefined) => IStatusInfo;
  handleRefound: (entry: IFinancialEntry) => void;
  handleCopyEntry: (entry: IFinancialEntry) => void;
  handlePayEntry: (entry: IFinancialEntry) => void;
  orderTable: Iordem;
  setOrderTable: Dispatch<SetStateAction<Iordem>>;
}

type fieldOrder = "tipo" | "numero_documento" | "date_initial" | "status"
  | "codigo_estabelecimento" | "descricao" | "conta_financeira"
  | "valor_parcela" | "parcela" | "data_vencimento" | "data_pagamento"
  | "uid_cliente" | "tipo_pagamento";

const FinancialEntriesList = ({
  tbodyRef, rows, handleCancelEntry, statusMapper,
  handleRefound, handleCopyEntry, handlePayEntry, orderTable, setOrderTable,
}: prop) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const dateForm = (dateString: string | Date | undefined) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" });
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return "R$ 0,00";
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(numericValue);
  };

  const getPartyName = (item: IFinancialEntry) => {
    if (item.tipo === "receber") return (item.cliente as any)?.nome_fantasia || item.cliente?.nome || "-";
    if (item.tipo === "pagar") return item.fornecedor?.nome_fantasia || "-";
    return "---";
  };

  const handleChangeOrder = (field: fieldOrder) => {
    setOrderTable({ [field]: orderTable[field] === "ASC" ? "DESC" : "ASC" });
  };

  const renderSortableHeader = (field: fieldOrder, label: string) => (
    <Button variant="textButton" onClick={() => handleChangeOrder(field)}
      title={orderTable[field] === "DESC" ? "Decrescente" : "Crescente"}>
      {label}
      {orderTable[field] ? (orderTable[field] === "DESC" ? <ArrowDropDown /> : <ArrowDropUp />) : ""}
    </Button>
  );

  // ── Mobile: cards ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className={styles.cardList}>
        {rows.length === 0 && <p className={styles.emptyMessage}>Nenhum registro encontrado.</p>}
        {rows.map((item) => {
          const statusInfo = statusMapper(item.status);
          const isCancellable = item.status !== "cancelado" && item.status !== "pago";
          const editUrl = `edit/${item.uid_financeiro_lancamento}`;
          return (
            <div key={item.uid_financeiro_lancamento} className={styles.card} onClick={() => navigate(editUrl)}>
              <div className={styles.cardHeader}>
                <span className={styles.cardFavorecido}>
                  {getPartyName(item) !== "-" ? getPartyName(item) : item.descricao}
                </span>
                <span className={`${styles.statusBadge} ${styles[`status-${statusInfo.color}`]}`}>
                  {statusInfo.text}
                </span>
              </div>
              {getPartyName(item) !== "-" && <p className={styles.cardDescricao}>{item.descricao}</p>}
              <div className={styles.cardRow}>
                <div className={styles.cardDataItem}>
                  <span className={styles.cardDataLabel}>Valor</span>
                  <strong className={styles.cardValor}>{formatCurrency(item.valor_parcela)}</strong>
                </div>
                <div className={styles.cardDataItem}>
                  <span className={styles.cardDataLabel}>Vencimento</span>
                  <span>{dateForm(item.data_vencimento)}</span>
                </div>
                {item.numero_documento && (
                  <div className={styles.cardDataItem}>
                    <span className={styles.cardDataLabel}>Doc.</span>
                    <span>{item.numero_documento}</span>
                  </div>
                )}
              </div>
              <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                <Button variant="contain" typeStyle="small" title="Extornar" onClick={() => handleRefound(item)}
                  disabled={item.status === "aberto" || item.status === "atrasado"}>
                  <CurrencyExchange fontSize="small" />
                </Button>
                <Button variant="contain" typeStyle="small" title="Copiar" onClick={() => handleCopyEntry(item)}>
                  <ContentCopy fontSize="small" />
                </Button>
                <Button variant="contain" typeStyle="small" title="Baixar" onClick={() => handlePayEntry(item)}
                  disabled={!(item.status === "aberto" || item.status === "atrasado")}>
                  <LocalAtm fontSize="small" />
                </Button>
                <Button variant="contain" typeStyle={["small", "cancel"]} title="Cancelar"
                  onClick={() => handleCancelEntry(item.uid_financeiro_lancamento)} disabled={!isCancellable}>
                  <CancelPresentation fontSize="small" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Desktop: tabela ──────────────────────────────────────────────────────────
  return (
    <div className={styles.containerTable}>
      <TableContainer
        tbodyRef={tbodyRef}
        columns={[
          { key: "codEx", titulo: renderSortableHeader("codigo_estabelecimento", "Código"), classStyle: styles.columnCod },
          { key: "clienteEx", titulo: renderSortableHeader("uid_cliente", "Cliente"), classStyle: styles.columnClient },
          { key: "descricaoEx", titulo: renderSortableHeader("descricao", "Descrição"), classStyle: styles.columnDescription },
          { key: "numDocEx", titulo: renderSortableHeader("numero_documento", "Doc."), classStyle: styles.columnNumDoc },
          { key: "valorEx", titulo: renderSortableHeader("valor_parcela", "Valor"), classStyle: styles.columnAmount },
          { key: "vencimentoEx", titulo: renderSortableHeader("data_vencimento", "Vencimento"), classStyle: styles.columnExpiration },
          { key: "statusEx", titulo: renderSortableHeader("status", "Status"), classStyle: styles.columnStatus },
          { key: "actionEx", titulo: "Ação", classStyle: styles.columnAction },
        ]}
        content={rows?.map((item) => {
          const statusInfo = statusMapper(item.status);
          const isCancellable = item.status !== "cancelado" && item.status !== "pago";
          const editUrl = `edit/${item.uid_financeiro_lancamento}`;
          return {
            values: {
              codEx: item.codigo_estabelecimento,
              clienteEx: getPartyName(item),
              descricaoEx: item.descricao,
              numDocEx: item.numero_documento || "-",
              valorEx: <strong>{formatCurrency(item.valor_parcela)}</strong>,
              vencimentoEx: dateForm(item.data_vencimento),
              statusEx: <span className={`${styles.statusBadge} ${styles[`status-${statusInfo.color}`]}`}>{statusInfo.text}</span>,
            },
            buttons: [
              { variant: "contain", typeStyle: "small", title: "Extornar pagamento", onClick: () => handleRefound(item), disabled: item.status === "aberto" || item.status === "atrasado", icon: <CurrencyExchange /> },
              { variant: "contain", typeStyle: "small", title: "Copiar para nova conta a receber", onClick: () => handleCopyEntry(item), icon: <ContentCopy /> },
              { variant: "contain", typeStyle: "small", title: "Baixar", onClick: () => handlePayEntry(item), disabled: !(item.status === "aberto" || item.status === "atrasado"), icon: <LocalAtm /> },
              { variant: "contain", typeStyle: ["small", "cancel"], title: "Cancelar lançamento", onClick: () => handleCancelEntry(item.uid_financeiro_lancamento), disabled: !isCancellable, icon: <CancelPresentation /> },
            ],
            onClick: () => navigate(editUrl),
          };
        }) ?? []}
      />
    </div>
  );
};

export default FinancialEntriesList;